import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from 'app/AppStore';
import Filter from 'components/Filter';
import { getFiltersMap, getStockFilterOptions } from 'components/Filter/Filter.types';
import FilterQuantity from 'components/Filter/FilterQuantity';
import FilterTags from 'components/Filter/FilterTags';
import {
  fetchUniversalProductsDataRequestSaga,
  fetchUniversalProductsSearchRequestSaga,
  getUniversalProducts,
  getUniversalProductsFilter,
  getUniversalProductsFilters,
  getUniversalProductsPagedState,
  resetUniversalProductsFilters,
  resetUniversalProductsPaged,
  setUniversalProductsRangeFilter,
  setUniversalProductsTextFilter,
} from 'domains/catalog/Catalog.store';
import { UNIVERSAL_PRODUCTS } from 'domains/catalog/Catalog.types';
import { getLoadedPrices } from 'domains/references';
import { useFetchPrices, useFetchReferencesStocks } from 'domains/references/References.requests';
import {
  useFilterPresetFromQueryParams,
  useUpdateProductsUrlQueryParams,
} from 'pages/UniversalProductsPage/ProductsPage/Product.hooks';
import { getExtendedFacetMaps } from 'pages/UniversalProductsPage/ProductsPage/Product.utils';
import ProductsSearch from 'pages/UniversalProductsPage/ProductsPage/ProductsSearch';
import {
  getUniversalProductsDisplayCount,
  shouldShowUniversalProductsEmptyState,
} from 'pages/UniversalProductsPage/ProductsPage/UniversalProducts.utils';
import { UniversalProductsCards } from 'pages/UniversalProductsPage/ProductsPage/UniversalProductsCards';
import { ErrorAlert, Flex, MarginBox } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { hasData, isLoading } from 'utils';
import { usePricelessFilter } from 'utils/hooks/usePricelessFilter';

const PAGE_SIZE = 15;

/**
 * Split Universal Products flow:
 * - Search: category cache + tech/brand facets
 * - Filters: progressive delivery/price facets merge
 * - Data: server pages via Show more (no infinite scroll)
 */
const ProductsContainerPaged = ({ nodeId }: { nodeId: string }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const pagedState = useSelector(getUniversalProductsPagedState);
  const displayReferences = pagedState.displayReferences;
  const [isSearchFilterActive, setIsSearchFilterActive] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const productsMap = useSelector((state: RootState) => getUniversalProducts(state, displayReferences));
  const productsDetails = useMemo(() => Array.from(productsMap.values()), [productsMap]);
  const isAnyReferenceLoading = pagedState.loading || productsDetails.some((product) => isLoading(product));
  const prices = useSelector((state: RootState) => getLoadedPrices(state, displayReferences));
  const somePagePriceMissing = prices.some((priceEntry) => isLoading(priceEntry.prices));
  const universalProductsFilters = useSelector(getUniversalProductsFilters);
  const filters = useSelector(getUniversalProductsFilter);

  const priceItems = useMemo(
    () =>
      prices.map((priceEntry) => ({
        id: priceEntry.reference,
        hasPriceData: hasData(priceEntry.prices) || isLoading(priceEntry.prices),
      })),
    [prices],
  );
  const { shouldIncludePriceless } = usePricelessFilter(priceItems, false);

  const visibleReferences = useMemo(() => {
    if (shouldIncludePriceless) {
      return displayReferences;
    }
    const priceByReference = new Map(prices.map((priceEntry) => [priceEntry.reference, priceEntry]));
    return displayReferences.filter((referenceNumber) => {
      const priceState = priceByReference.get(referenceNumber)?.prices;
      return hasData(priceState) || isLoading(priceState);
    });
  }, [displayReferences, shouldIncludePriceless, prices]);

  const displayedProductCount = getUniversalProductsDisplayCount({
    referencesLength: pagedState.total,
    displayReferencesLength: pagedState.total,
    referencesLoading: pagedState.loading,
  });

  const shouldShowNoResults = shouldShowUniversalProductsEmptyState({
    currentDataLength: pagedState.total,
    isAnyReferenceLoading: pagedState.loading,
    somePriceMissing: false,
    someStockMissingFirstLoad: false,
    someStockMissing: false,
  });

  const allTextFilters = useMemo(() => {
    const facets = [...(universalProductsFilters.textFilters ?? [])];
    const hasDelivery = facets.some((facet) => facet.id === 'delivery');
    if (pagedState.tradingFacetsLoading && !hasDelivery) {
      facets.push(getStockFilterOptions(UNIVERSAL_PRODUCTS, false));
    }
    return facets.sort((left, right) => left.id.localeCompare(right.id));
  }, [universalProductsFilters.textFilters, pagedState.tradingFacetsLoading]);

  const allRangeFilters = useMemo(() => {
    const facets = [...(universalProductsFilters.rangeFilters ?? [])];
    const hasPrices = facets.some((facet) => facet.id === 'prices');
    if (pagedState.tradingFacetsLoading && !hasPrices) {
      facets.push({
        id: 'prices',
        type: 'RANGE',
        min: 0,
        max: 0,
        numberOfItems: 0,
        active: true,
      });
    }
    return facets.sort((left, right) => left.id.localeCompare(right.id));
  }, [universalProductsFilters.rangeFilters, pagedState.tradingFacetsLoading]);

  const tradingFacetLoadingIds = useMemo(() => (pagedState.tradingFacetsLoading ? ['delivery', 'prices'] : []), [
    pagedState.tradingFacetsLoading,
  ]);

  const requestSearch = useCallback(
    (searchQuery?: string) => {
      dispatch(
        fetchUniversalProductsSearchRequestSaga({
          nodeId,
          searchQuery,
        }),
      );
    },
    [dispatch, nodeId],
  );

  const requestDataPage = useCallback(
    (page: number) => {
      if (!pagedState.nodeId) {
        return;
      }
      dispatch(
        fetchUniversalProductsDataRequestSaga({
          nodeId: pagedState.nodeId,
          searchQuery: pagedState.searchQuery,
          page,
          pageSize: PAGE_SIZE,
        }),
      );
    },
    [dispatch, pagedState.nodeId, pagedState.searchQuery],
  );

  const handleSearch = useCallback(
    (searchQuery: string) => {
      if (searchQuery === '') {
        return;
      }
      setActiveSearchQuery(searchQuery);
      setIsSearchFilterActive(true);
      requestSearch(searchQuery);
    },
    [requestSearch],
  );

  const cancelSearch = useCallback(() => {
    setActiveSearchQuery('');
    setIsSearchFilterActive(false);
    requestSearch();
  }, [requestSearch]);

  const resetQueryParams = useCallback(() => {
    history.replace({
      pathname: location.pathname,
      search: '',
    });
  }, [history, location.pathname]);

  const handleShowMore = useCallback(() => {
    if (pagedState.loading || pagedState.displayReferences.length >= pagedState.total) {
      return;
    }
    requestDataPage(pagedState.page + 1);
  }, [pagedState.displayReferences.length, pagedState.loading, pagedState.page, pagedState.total, requestDataPage]);

  useFetchReferencesStocks(displayReferences);
  useFetchPrices(displayReferences);
  useFilterPresetFromQueryParams(allTextFilters);
  useUpdateProductsUrlQueryParams();

  useEffect(() => {
    dispatch(resetUniversalProductsPaged());
    dispatch(resetUniversalProductsFilters());
    setActiveSearchQuery('');
    setIsSearchFilterActive(false);
    requestSearch();
  }, [dispatch, nodeId, requestSearch]);

  const pagingResetKey = `${nodeId}:${activeSearchQuery}`;
  const hasMountedRef = useRef(false);
  // Price range facet is display-only until server-side range filtering exists — do not refetch on slider moves.
  const filtersSignature = useMemo(
    () =>
      [...filters.textFilters.entries()]
        .filter(([, values]) => values.length > 0)
        .map(([key, values]) => `${key}:${values.join('|')}`)
        .join(';'),
    [filters.textFilters],
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (!pagedState.nodeId) {
      return;
    }
    requestDataPage(1);
    // Intentionally only re-fetch data when text filters change (not when nodeId arrives after search).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSignature]);

  const isInitialLoading = pagedState.loading && visibleReferences.length === 0;
  const isShowMoreLoading = pagedState.loading && visibleReferences.length > 0;
  const hasMore = !pagedState.loading && pagedState.displayReferences.length < pagedState.total;

  return (
    <Flex direction={'column'}>
      <Flex>
        <Flex direction={'column'}>
          <FilterTags
            filters={filters}
            setTextFilters={(id, item) => dispatch(setUniversalProductsTextFilter({ id, item }))}
            setRangeFilters={(id, range) => dispatch(setUniversalProductsRangeFilter({ id, range }))}
          />
          <MarginBox mb={10} />
          <Flex direction={'row'} justify={'space-between'}>
            <FilterQuantity numberOfProducts={displayedProductCount} />
            <ProductsSearch
              isSearchFilterActive={isSearchFilterActive}
              isAnyReferenceLoading={isAnyReferenceLoading}
              handleSearch={handleSearch}
              cancelSearch={cancelSearch}
            />
            <div>
              <ViewTabs />
            </div>
          </Flex>
        </Flex>
      </Flex>
      <MarginBox mt={60}>
        <Flex gap={15}>
          <Flex direction={'column'} size={0}>
            <Filter
              filters={filters}
              textFilters={allTextFilters}
              rangeFilters={allRangeFilters}
              setTextFilters={(id, item) => dispatch(setUniversalProductsTextFilter({ id, item }))}
              setRangeFilters={(id, range) => dispatch(setUniversalProductsRangeFilter({ id, range }))}
              resetFilters={() => {
                dispatch(resetUniversalProductsFilters());
                resetQueryParams();
                requestDataPage(1);
              }}
              loading={pagedState.loading && visibleReferences.length === 0}
              loadingFacetIds={tradingFacetLoadingIds}
              facetsMap={getExtendedFacetMaps(getFiltersMap(t), allTextFilters)}
              type={UNIVERSAL_PRODUCTS}
              requiredMin={0}
            />
          </Flex>
          <Flex direction={'column'} size={1}>
            {shouldShowNoResults && !pagedState.loading && (
              <ErrorAlert
                message={t(
                  'catalog.universal_products.filter.notfound',
                  'Sorry, no match found for your research, please adjust your search criteria.',
                )}
              />
            )}
            <UniversalProductsCards
              displayReferences={visibleReferences}
              isDataStillLoading={isInitialLoading || (somePagePriceMissing && visibleReferences.length === 0)}
              pagingResetKey={pagingResetKey}
              serverShowMore={{
                hasMore,
                loading: isShowMoreLoading,
                onShowMore: handleShowMore,
              }}
            />
          </Flex>
        </Flex>
      </MarginBox>
    </Flex>
  );
};

export default ProductsContainerPaged;
